import { createAsyncThunk } from '@reduxjs/toolkit';

import { client } from 'src/graphql/client';
import { DISPATCH_PROVIDER_ROUTE } from 'src/graphql/requests/dispatchProviderRoute';
import {
  GDispatchProviderRouteMutation,
  GThirdPartyProvider,
} from 'src/graphql/types/generated';
import { logWarning } from 'src/services/datadog';
import type { AppDispatch } from 'src/state/store';
import { firstUserErrorMessage } from 'src/utils';
import {
  ProviderQuote,
  providerDisplayName,
  quoteProviderLabel,
  quoteTotalCents,
  quoteTotalLabel,
} from 'src/utils/thirdPartyDelivery';
import { setActiveRouteListTab, setRouteListCardVisible } from '.';
import { NotificationType, showNotification } from '../notification';
import { ProviderRouteResult } from './markRouteForProvider';
import { refetchRouteList } from './refetchRouteList';
import { RouteListTab } from './state';

type DispatchProviderRouteArgs = {
  routeId: string;
  routeName: string;
  provider: GThirdPartyProvider;
  quote: ProviderQuote;
  warehouseId: string;
  nightEnd: string;
};

export const dispatchProviderRoute = createAsyncThunk(
  'delivery/dispatchProviderRoute',
  async (
    {
      routeId,
      routeName,
      provider,
      quote,
      warehouseId,
      nightEnd,
    }: DispatchProviderRouteArgs,
    thunkAPI,
  ): Promise<ProviderRouteResult> => {
    const { data, error } = await client
      .mutation<GDispatchProviderRouteMutation>(DISPATCH_PROVIDER_ROUTE, {
        input: { routeId, quoteId: quote.id },
      })
      .toPromise();

    // Every outcome can change the route: a refusal by the provider puts it
    // back to quoted with the reason, and a lost response may hide a dispatch.
    refetchRouteList(thunkAPI.dispatch as AppDispatch, {
      warehouseId,
      nightEnd,
    });

    const payload = data?.dispatchProviderRoute;
    const userErrors = payload?.userErrors ?? [];

    if (userErrors.length > 0) {
      logWarning('third_party.dispatch_route_failed', {
        routeId,
        provider,
        userError: userErrors[0]?.__typename,
        transportError: false,
      });
      return { ok: false, message: firstUserErrorMessage(userErrors) };
    }

    // The dialog says it could not confirm and waits on its poll for the
    // truth, so there is no toast here.
    if (error || !payload) {
      logWarning('third_party.dispatch_route_failed', {
        routeId,
        provider,
        transportError: Boolean(error),
      });
      return { ok: false };
    }

    const price =
      quoteTotalCents(quote) === null ? '' : ` ${quoteTotalLabel(quote)}`;
    thunkAPI.dispatch(
      showNotification({
        type: NotificationType.Success,
        message: `${routeName} sent to ${providerDisplayName(
          provider,
        )} · ${quoteProviderLabel(quote)}${price}`,
      }),
    );
    // The GM lands on the Burq sub-tab, where the route's card now waits for
    // its driver beside the night's other Burq routes.
    thunkAPI.dispatch(setRouteListCardVisible());
    thunkAPI.dispatch(setActiveRouteListTab(RouteListTab.BurqDeliveries));
    return { ok: true };
  },
);
